import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';

import {
  buildGatewayConsultPayload,
  createSessionId,
  createTraceId,
  fetchConsultationPatients,
  fetchLatestUserReport,
  fetchJson,
  getResolvedUserNo,
  summarizePersonalContext,
  writeConsultTrace,
} from '../utils/agentUiApi';
import { markdownSanitizeOptions } from '../utils/markdownSanitizeOptions';
import './ChatConsultPage.css';

function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  try {
    return new Date(value).toLocaleString('ko-KR');
  } catch {
    return String(value);
  }
}

function RiskBadge({ value }) {
  return <span className={`consult-risk consult-risk-${value || 'unknown'}`}>{value || 'unknown'}</span>;
}

function normalizeAssistantMarkdown(content) {
  if (!content) {
    return '';
  }

  const normalizedLines = String(content)
    .replace(/\r\n/g, '\n')
    .replace(/\u00a0/g, ' ')
    .split('\n')
    .map((line) => line.replace(/^\*\s+/, '- ').replace(/^-\s+/, '- ').replace(/^\s+$/, ''));

  const rebuilt = [];
  let prevKind = 'blank';

  const getLineKind = (line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      return 'blank';
    }
    if (/^[-*]\s+/.test(trimmed)) {
      return 'list';
    }
    if (/^\d+\.\s+/.test(trimmed)) {
      return 'ordered_list';
    }
    if (/^>/.test(trimmed)) {
      return 'quote';
    }
    return 'paragraph';
  };

  normalizedLines.forEach((line) => {
    const kind = getLineKind(line);
    if (kind === 'blank') {
      if (rebuilt.length && rebuilt[rebuilt.length - 1] !== '') {
        rebuilt.push('');
      }
      prevKind = 'blank';
      return;
    }

    if ((kind === 'list' || kind === 'ordered_list') && prevKind === 'paragraph' && rebuilt[rebuilt.length - 1] !== '') {
      rebuilt.push('');
    } else if (kind === 'paragraph' && (prevKind === 'list' || prevKind === 'ordered_list' || prevKind === 'quote') && rebuilt[rebuilt.length - 1] !== '') {
      rebuilt.push('');
    }

    rebuilt.push(line.trim());
    prevKind = kind;
  });

  while (rebuilt.length && rebuilt[rebuilt.length - 1] === '') {
    rebuilt.pop();
  }

  return rebuilt.join('\n');
}

function ChatMessageContent({ role, content }) {
  if (role !== 'assistant') {
    return <p>{content}</p>;
  }

  return (
    <div className="chat-consult-message-content markdown-body chat-consult-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeOptions]]}
      >
        {normalizeAssistantMarkdown(content)}
      </ReactMarkdown>
    </div>
  );
}

function getPatientOptionId(patient) {
  const raw = patient?.std_patient_id ?? patient?.patient_id ?? patient?.id;
  return raw === null || raw === undefined ? '' : String(raw);
}

function formatPatientOptionLabel(patient) {
  const id = getPatientOptionId(patient);
  const rawName = patient?.name || patient?.patient_name || patient?.display_name || patient?.label || '';
  const name = String(rawName || '').trim() || `환자 ${id}`;
  return `가족 ${id} / ${name}`;
}

function sameScopeId(left, right) {
  if (left === null || left === undefined || right === null || right === undefined) {
    return false;
  }
  return String(left) === String(right);
}

function ChatConsultPage() {
  const [patientId, setPatientId] = useState('');
  const [patients, setPatients] = useState([]);
  const [patientsError, setPatientsError] = useState('');
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [sessionId, setSessionId] = useState(() => createSessionId());
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [latestResponse, setLatestResponse] = useState(null);
  const [latestReport, setLatestReport] = useState(null);
  const [latestReportError, setLatestReportError] = useState('');
  const [loadingLatestReport, setLoadingLatestReport] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const userNo = getResolvedUserNo();
  const patientOptions = useMemo(() => {
    const normalized = patients
      .map((patient) => ({
        id: getPatientOptionId(patient),
        label: formatPatientOptionLabel(patient),
        isDefault: !!patient?.is_default,
      }))
      .filter((patient) => patient.id);

    if (patientId && !normalized.some((patient) => patient.id === patientId)) {
      return [
        { id: patientId, label: `가족 ${patientId} / 리포트 기준 환자`, isDefault: false },
        ...normalized,
      ];
    }

    return normalized;
  }, [patients, patientId]);
  const patientLabelById = useMemo(
    () => new Map(patientOptions.map((patient) => [patient.id, patient.label])),
    [patientOptions],
  );
  const selectedPatientLabel = patientLabelById.get(patientId) || (patientId ? `가족 ${patientId}` : '');
  const messageHistory = useMemo(
    () =>
      messages.map((item) => ({
        role: item.role,
        content: item.content,
      })),
    [messages],
  );
  const displayedUserNo = userNo ? `로그인 사용자(user_no) / ${userNo}` : '';
  const displayedReportId = latestReport?.report_id ? String(latestReport.report_id) : '';

  useEffect(() => {
    let cancelled = false;

    const initializePatientScope = async () => {
      if (!userNo) {
        setPatients([]);
        setPatientId('');
        setPatientsError('');
        setLatestReport(null);
        setLatestReportError('');
        return;
      }

      setLoadingPatients(true);
      setPatientsError('');
      setLoadingLatestReport(true);
      setLatestReportError('');
      try {
        const [patientList, userLatestReport] = await Promise.all([
          fetchConsultationPatients(userNo),
          fetchLatestUserReport(userNo),
        ]);
        if (cancelled) {
          return;
        }

        const normalizedPatients = Array.isArray(patientList) ? patientList : [];
        const latestPatientId = userLatestReport?.patient_id ? String(userLatestReport.patient_id) : '';
        const defaultPatient = normalizedPatients.find((patient) => patient?.is_default) || normalizedPatients[0];
        const defaultPatientId = latestPatientId || getPatientOptionId(defaultPatient);

        setPatients(normalizedPatients);
        setPatientId(defaultPatientId);
        setSessionId(createSessionId());
        setMessages([]);
        setLatestResponse(null);
        setQuestion('');
        setError('');

        if (!defaultPatientId) {
          setLatestReport(null);
          setLatestReportError('등록된 환자 정보가 없습니다.');
        }
      } catch (fetchError) {
        if (!cancelled) {
          setPatients([]);
          setPatientId('');
          setPatientsError(fetchError?.message || '환자 목록을 불러오지 못했습니다.');
          setLatestReport(null);
          setLatestReportError(fetchError?.message || '최신 리포트 조회에 실패했습니다.');
        }
      } finally {
        if (!cancelled) {
          setLoadingPatients(false);
          setLoadingLatestReport(false);
        }
      }
    };

    initializePatientScope();

    return () => {
      cancelled = true;
    };
  }, [userNo]);

  useEffect(() => {
    let cancelled = false;

    const loadLatestReportForPatient = async () => {
      if (!userNo || !patientId) {
        setLatestReport(null);
        return;
      }

      setLoadingLatestReport(true);
      setLatestReportError('');
      try {
        const report = await fetchLatestUserReport(userNo, patientId);
        if (!cancelled) {
          setLatestReport(report);
          setLatestReportError(report?.report_id ? '' : '선택한 환자의 최신 non-superseded 리포트를 찾을 수 없습니다.');
        }
      } catch (fetchError) {
        if (!cancelled) {
          setLatestReport(null);
          setLatestReportError(fetchError?.message || '최신 리포트 조회에 실패했습니다.');
        }
      } finally {
        if (!cancelled) {
          setLoadingLatestReport(false);
        }
      }
    };

    loadLatestReportForPatient();

    return () => {
      cancelled = true;
    };
  }, [userNo, patientId]);

  const handleResetSession = () => {
    setSessionId(createSessionId());
    setMessages([]);
    setLatestResponse(null);
    setQuestion('');
    setError('');
  };

  const handlePatientChange = (event) => {
    const nextPatientId = event.target.value;
    if (nextPatientId === patientId) {
      return;
    }
    setPatientId(nextPatientId);
    setSessionId(createSessionId());
    setMessages([]);
    setLatestResponse(null);
    setQuestion('');
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!patientId.trim()) {
      setError('상담 대상 환자를 선택해 주세요.');
      return;
    }
    if (!question.trim()) {
      setError('질문을 입력해 주세요.');
      return;
    }

    setSubmitting(true);
    setError('');
    const traceId = createTraceId();
    const traceCreatedAt = new Date().toISOString();
    let payload = buildGatewayConsultPayload({
      patientId,
      reportId: latestReport?.report_id ?? undefined,
      question,
      sessionId,
      history: messageHistory,
    });
    try {
      // 상담 요청 시점마다 최신 non-superseded 리포트를 다시 조회해 stale report_id를 막는다.
      setLoadingLatestReport(true);
      setLatestReportError('');
      let resolvedLatestReport = latestReport;
      let latestReportFetchError = null;
      try {
        const fetchedLatestReport = await fetchLatestUserReport(userNo, patientId);
        resolvedLatestReport = fetchedLatestReport ?? latestReport;
      } catch (fetchLatestReportError) {
        latestReportFetchError = fetchLatestReportError;
        resolvedLatestReport = latestReport;
      }
      setLatestReport(resolvedLatestReport);

      if (!resolvedLatestReport?.report_id) {
        const message = latestReportFetchError
          ? `최신 리포트 조회에 실패했습니다. ${latestReportFetchError.message || ''}`.trim()
          : '최신 non-superseded 리포트를 찾을 수 없습니다.';
        setLatestReportError(message);
        throw new Error(message);
      }

      payload = buildGatewayConsultPayload({
        patientId,
        reportId: resolvedLatestReport?.report_id ?? undefined,
        question,
        sessionId,
        history: messageHistory,
      });

      const response = await fetchJson('/api/v1/chatbot/poc', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const submittedQuestion = question.trim();
      setMessages((prev) => [
        ...prev,
        { id: `${Date.now()}-user`, role: 'user', content: submittedQuestion, createdAt: new Date().toISOString() },
        {
          id: `${Date.now()}-assistant`,
          role: 'assistant',
          content: response.answer,
          createdAt: new Date().toISOString(),
          meta: {
            intent: response.intent,
            risk_level: response.risk_level,
          },
        },
      ]);
      setLatestResponse(response);
      setQuestion('');

      writeConsultTrace({
        id: traceId,
        createdAt: traceCreatedAt,
        userNo: userNo ? Number(userNo) : null,
        scope: {
          patient_id: payload.patient_id,
          patient_label: selectedPatientLabel,
          report_id: payload.report_id,
          session_id: payload.session_id,
        },
        request: payload,
        response,
      });
    } catch (submitError) {
      const message = submitError.message || '상담 요청에 실패했습니다.';
      setError(message);
      writeConsultTrace({
        id: traceId,
        createdAt: traceCreatedAt,
        userNo: userNo ? Number(userNo) : null,
        scope: {
          patient_id: payload.patient_id,
          patient_label: selectedPatientLabel,
          report_id: payload.report_id,
          session_id: payload.session_id,
        },
        request: payload,
        response: {
          error: message,
          trace: {
            forwarded_payload: payload,
          },
        },
      });
    } finally {
      setLoadingLatestReport(false);
      setSubmitting(false);
    }
  };

  const personalSummary = summarizePersonalContext(latestResponse?.personal_context);
  const memoryHits = useMemo(
    () => (Array.isArray(latestResponse?.personal_context?.memory_hits)
      ? latestResponse.personal_context.memory_hits
      : []),
    [latestResponse],
  );
  const displayedMemoryHits = [...memoryHits].sort((left, right) => {
    const leftPriority = left?.content_type === 'std_observation' ? 0 : 1;
    const rightPriority = right?.content_type === 'std_observation' ? 0 : 1;
    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority;
    }
    return 0;
  });
  const contextScopeIssues = useMemo(() => {
    if (!latestResponse || !patientId) {
      return [];
    }

    const issues = [];
    const forwardedPatientId = latestResponse.trace?.forwarded_payload?.patient_id;
    const reportContextPatientId = latestResponse.report_context?.patient_id;

    if (forwardedPatientId !== undefined && !sameScopeId(forwardedPatientId, patientId)) {
      issues.push(`Gateway forwarded patient_id ${forwardedPatientId}가 선택 가족 ${patientId}와 다릅니다.`);
    }
    if (reportContextPatientId !== undefined && reportContextPatientId !== null && !sameScopeId(reportContextPatientId, patientId)) {
      issues.push(`Report Context patient_id ${reportContextPatientId}가 선택 가족 ${patientId}와 다릅니다.`);
    }

    memoryHits.forEach((item) => {
      if (item?.patient_id !== undefined && item?.patient_id !== null && !sameScopeId(item.patient_id, patientId)) {
        issues.push(`Memory hit #${item.id || '-'} patient_id ${item.patient_id}가 선택 가족 ${patientId}와 다릅니다.`);
      }
    });

    return issues;
  }, [latestResponse, memoryHits, patientId]);
  const reportInputPlaceholder = loadingLatestReport
    ? '최신 리포트 조회 중'
    : latestReportError
      ? '최신 리포트 조회 실패'
      : '최신 리포트 없음';

  return (
    <div className="chat-consult-page">
      <section className="chat-consult-hero">
        <div>
          <p className="chat-consult-eyebrow">UI-CONSULT-01</p>
          <h1>상담 채팅 화면 MVP</h1>
          <p className="chat-consult-description">
            질문 입력, 응답 수신, `session_id`/`report_id`/`patient_id` 재현, guardrail 및 컨텍스트 확인까지 한 화면에서 검증합니다.
          </p>
        </div>
        <div className="chat-consult-links">
          <Link to="/console/gateway-trace" className="chat-consult-link">
            Gateway Trace 보기
          </Link>
          <Link to="/console/identity" className="chat-consult-link">
            Identity Console 보기
          </Link>
        </div>
      </section>

      <section className="chat-consult-grid">
        <aside className="chat-consult-sidebar">
          <form className="chat-consult-form" onSubmit={handleSubmit}>
            <h2>검증 입력</h2>
            <label>
              로그인 사용자(user_no)
              <input id="chat-consult-user-no" name="user_no" value={displayedUserNo} readOnly />
            </label>
            <label>
              가족(patient_id)
              <select
                id="chat-consult-patient-id"
                name="patient_id"
                value={patientId}
                onChange={handlePatientChange}
                disabled={loadingPatients || patientOptions.length === 0}
              >
                <option value="">
                  {loadingPatients ? '환자 목록을 불러오는 중입니다.' : '상담 대상 환자를 선택해 주세요.'}
                </option>
                {patientOptions.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.label}
                  </option>
                ))}
              </select>
            </label>
            {patientsError ? (
              <p className="chat-consult-subtext chat-consult-error">{patientsError}</p>
            ) : null}
            <label>
              report_id
              <input id="chat-consult-report-id" name="report_id" value={displayedReportId} readOnly placeholder={reportInputPlaceholder} />
            </label>
            <p className="chat-consult-subtext">
              환자를 변경하면 새 session_id를 발급하고, 선택 환자의 최신 non-superseded 리포트를 자동으로 사용합니다.
            </p>
            {latestReportError ? (
              <p className="chat-consult-subtext chat-consult-error">{latestReportError}</p>
            ) : null}
            <label>
              session_id
              <input id="chat-consult-session-id" name="session_id" value={sessionId} onChange={(event) => setSessionId(event.target.value)} />
            </label>
            <label>
              질문
              <textarea
                id="chat-consult-question"
                name="question"
                rows={6}
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                placeholder="예: 최근 혈압이 160까지 올라가고 당뇨 관리가 걱정돼요."
              />
            </label>
            {error ? <div className="chat-consult-error">{error}</div> : null}
            <div className="chat-consult-actions">
              <button
                type="submit"
                className="chat-consult-submit"
                disabled={submitting || loadingPatients || loadingLatestReport || !patientId || !displayedReportId}
              >
                {submitting ? '상담 요청 중...' : loadingLatestReport ? '리포트 확인 중...' : '상담 요청'}
              </button>
              <button type="button" className="chat-consult-reset" onClick={handleResetSession}>
                새 세션
              </button>
            </div>
          </form>

          <div className="chat-consult-panel">
            <h2>최근 응답 요약</h2>
            {latestResponse ? (
              <dl className="chat-consult-summary-list">
                <div>
                  <dt>intent</dt>
                  <dd>{latestResponse.intent}</dd>
                </div>
                <div>
                  <dt>risk</dt>
                  <dd><RiskBadge value={latestResponse.risk_level} /></dd>
                </div>
                <div>
                  <dt>session_id</dt>
                  <dd>{latestResponse.session_id || sessionId}</dd>
                </div>
                <div>
                  <dt>report_id</dt>
                  <dd>{latestResponse.report_id || '-'}</dd>
                </div>
                <div>
                  <dt>dec_id</dt>
                  <dd>{latestResponse.dec_id || '-'}</dd>
                </div>
                <div>
                  <dt>created_at</dt>
                  <dd>{formatDateTime(latestResponse.created_at)}</dd>
                </div>
              </dl>
            ) : (
              <p className="chat-consult-empty">상담을 실행하면 최신 응답 요약이 표시됩니다.</p>
            )}
          </div>
        </aside>

        <main className="chat-consult-main">
          <section className="chat-consult-panel">
            <h2>대화 흐름</h2>
            <div className="chat-consult-messages">
              {messages.length === 0 ? (
                <p className="chat-consult-empty">아직 대화가 없습니다. 왼쪽 입력 폼에서 첫 질문을 보내 주세요.</p>
              ) : (
                messages.map((message) => (
                  <article key={message.id} className={`chat-consult-message chat-consult-message-${message.role}`}>
                    <header>
                      <strong>{message.role === 'user' ? '사용자' : 'AI 응답'}</strong>
                      <span>{formatDateTime(message.createdAt)}</span>
                    </header>
                    <ChatMessageContent role={message.role} content={message.content} />
                    {message.meta ? (
                      <div className="chat-consult-inline-meta">
                        <span>{message.meta.intent}</span>
                        <RiskBadge value={message.meta.risk_level} />
                      </div>
                    ) : null}
                  </article>
                ))
              )}
            </div>
          </section>

          <section className="chat-consult-detail-grid">
            <article className="chat-consult-panel">
              <h2>Guardrail / 운영 신호</h2>
              {latestResponse?.guardrail ? (
                <>
                  <div className="chat-consult-chip-row">
                    <span className="chat-consult-chip">{latestResponse.guardrail.category}</span>
                    <span className="chat-consult-chip">{latestResponse.guardrail.template_key}</span>
                    {latestResponse.operational_signals?.response_mode ? (
                      <span className="chat-consult-chip">{latestResponse.operational_signals.response_mode}</span>
                    ) : null}
                  </div>
                  <dl className="chat-consult-keyvalue">
                    <div><dt>disclaimer</dt><dd>{latestResponse.guardrail.disclaimer || '-'}</dd></div>
                    <div><dt>followup</dt><dd>{latestResponse.followup_action || latestResponse.guardrail.followup_message || '-'}</dd></div>
                    <div><dt>escalation</dt><dd>{String(!!latestResponse.guardrail.escalation_recommended)}</dd></div>
                    <div><dt>contact</dt><dd>{latestResponse.guardrail.escalation_contact || '-'}</dd></div>
                    <div><dt>rag_status</dt><dd>{latestResponse.operational_signals?.rag_status || '-'}</dd></div>
                    <div><dt>rag_reason</dt><dd>{latestResponse.operational_signals?.rag_reason || '-'}</dd></div>
                  </dl>
                </>
              ) : (
                <p className="chat-consult-empty">guardrail 정보가 아직 없습니다.</p>
              )}
            </article>

            <article className="chat-consult-panel">
              <h2>Context 확인</h2>
              {latestResponse ? (
                <div className="chat-consult-context-stack">
                  <div className={`chat-consult-scope-banner ${contextScopeIssues.length ? 'is-warning' : 'is-ok'}`}>
                    <strong>{contextScopeIssues.length ? '환자 컨텍스트 점검 필요' : '환자 컨텍스트 일치'}</strong>
                    <span>
                      {contextScopeIssues.length
                        ? contextScopeIssues[0]
                        : `${selectedPatientLabel || `가족 ${patientId}`} 기준으로 report, memory, trace를 확인했습니다.`}
                    </span>
                  </div>
                  <div>
                    <h3>Report Context</h3>
                    <p>{latestResponse.report_context?.title || '제목 없음'}</p>
                    <p className="chat-consult-subtext">
                      환자 {latestResponse.report_context?.patient_id || patientId || '-'} · 리포트 {latestResponse.report_context?.report_id || latestResponse.report_id || displayedReportId || '-'}
                    </p>
                    <p className="chat-consult-subtext">{latestResponse.report_context?.summary || latestResponse.report_context?.recommendation_overview || '-'}</p>
                  </div>
                  <div>
                    <h3>Personal Summary</h3>
                    {personalSummary.length ? (
                      <ul>
                        {personalSummary.map((line, index) => <li key={`${line}-${index}`}>{line}</li>)}
                      </ul>
                    ) : (
                      <p className="chat-consult-subtext">요약 없음</p>
                    )}
                  </div>
                  <div>
                    <h3>Memory Hits</h3>
                    {memoryHits.length ? (
                      <ul>
                        {displayedMemoryHits.slice(0, 3).map((item) => (
                          <li key={item.id || item.source_key || item.content_text}>
                            <strong>{item.content_type || 'memory'}</strong>
                            <em>환자 {item.patient_id || patientId || '-'} · 리포트 {item.report_id || item.source_id || '-'}</em>
                            <span>{item.excerpt || item.content_text || item.summary || '-'}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="chat-consult-subtext">memory_hits 없음</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="chat-consult-empty">응답을 받으면 report/personal context가 표시됩니다.</p>
              )}
            </article>

            <article className="chat-consult-panel">
              <h2>근거 / Trace 스냅샷</h2>
              {latestResponse ? (
                <div className="chat-consult-context-stack">
                  <div>
                    <h3>Evidence</h3>
                    {Array.isArray(latestResponse.evidence) && latestResponse.evidence.length ? (
                      <ul>
                        {latestResponse.evidence.map((item, index) => (
                          <li key={`${item.title}-${index}`}>
                            <strong>{item.title}</strong>
                            <span>{item.summary}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="chat-consult-subtext">근거 없음</p>
                    )}
                  </div>
                  <div>
                    <h3>Forwarded Payload</h3>
                    <pre>{JSON.stringify(latestResponse.trace?.forwarded_payload || {}, null, 2)}</pre>
                  </div>
                </div>
              ) : (
                <p className="chat-consult-empty">응답을 받으면 trace 스냅샷이 표시됩니다.</p>
              )}
            </article>
          </section>
        </main>
      </section>
    </div>
  );
}

export default ChatConsultPage;
